<template>
  <div class="svg-three-animation__wrapper">
    <div class="svg-three-animation__container">
      <canvas></canvas>
    </div>
  </div>
</template>

<script>
import * as THREE from "three";
import createLoop from "canvas-loop";
import loadSvg from "load-svg";
import Tweenr from "tweenr";
import { parse as getSvgPaths } from "extract-svg-path";
import * as randomVec3 from "gl-vec3/random";
import triangleCentroid from "triangle-centroid";
import reindex from "mesh-reindex";
import unindex from "unindex-mesh";
import svgMesh3d from "svg-mesh-3d";
import fragShader from "@/modules/home/components/fragShader";
import vertShader from "@/modules/home/components/vertShader";

export default {
  name: "svg-three-animation",
  data() {
    return {
      canvas: null,
      renderer: null,
      scene: null,
      camera: null,
      tweenr: null,
      app: null,
      pointer: 0,
      mesh: null,
      attributes: null
    };
  },
  mounted() {
    this.tweenr = Tweenr({ defaultEase: "expoOut" });
    this.canvas = this.$el.querySelector("canvas");
    this.canvas.addEventListener("touchstart", ev => ev.preventDefault());
    this.canvas.addEventListener("contextmenu", ev => ev.preventDefault());
    this.renderer = new THREE.WebGL1Renderer({
      canvas: this.canvas,
      antialias: true,
      devicePixelRatio: window.devicePixelRatio
    });
    this.renderer.setClearColor(0xecf0f3, 1);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.01, 100);
    this.camera.position.set(0, 0, 5);

    this.createApp();
    this.nextSvgMesh();
  },
  methods: {
    nextSvgMesh(delay, beforeRender) {
      delay = delay || 0;
      const svgs = [
        "anh-nguyen.svg",
        "frontend-dev.svg",
        "vuejs.svg",
        "chrome.svg"
      ];
      const current = svgs[this.pointer++ % svgs.length];

      loadSvg(`./svg/${current}`, (err, svg) => {
        if (beforeRender) {
          beforeRender();
        }
        this.renderSvg(svg, delay);
      });
    },
    renderSvg(svg, delay) {
      delay = delay || 0;

      const wireframe = this.pointer % 2 === 0;

      // grab all <path> data
      const svgPath = getSvgPaths(svg);
      // triangulate
      let complex = svgMesh3d(svgPath, {
        scale: 10,
        simplify: 0.5,
        // play with this value for different aesthetic
        randomization: 100
      });

      // split mesh into separate triangles so no vertices are shared
      complex = reindex(unindex(complex.positions, complex.cells));

      // we will animate the triangles in the vertex shader
      this.attributes = this.getAnimationAttributes(
        complex.positions,
        complex.cells
      );

      // build a ThreeJS geometry from the mesh primitive
      const geometry = new THREE.BufferGeometry();
      const vertices = new Float32Array(complex.positions.length * 3);
      for (let i = 0, len = complex.positions.length; i < len; i++) {
        const pos = complex.positions[i];
        vertices[i * 3] = pos[0];
        vertices[i * 3 + 1] = pos[1];
        vertices[i * 3 + 2] = pos[2];
      }
      const faces = new Float32Array(complex.cells.length);
      for (let i = 0, len = complex.cells.length; i < len; i++) {
        const face = complex.cells[i];
        faces[i] = new THREE.Face3(face[0], face[1], face[2]);
      }
      geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
      geometry.setAttribute("face", new THREE.BufferAttribute(faces, 1));

      geometry.setAttribute(
        "direction",
        new THREE.BufferAttribute(this.attributes.direction.value, 3)
      );
      geometry.setAttribute(
        "centroid",
        new THREE.BufferAttribute(this.attributes.centroid.value, 3)
      );
      // our shader material
      const material = new THREE.ShaderMaterial({
        side: THREE.DoubleSide,
        vertexShader: vertShader,
        fragmentShader: fragShader,
        wireframe: wireframe,
        transparent: true,
        uniforms: {
          opacity: { type: "f", value: 1 },
          scale: { type: "f", value: 0 },
          animate: { type: "f", value: 0 },
          diffuseR: { type: "f", value: 66 / 255 },
          diffuseG: { type: "f", value: 185 / 255 },
          diffuseB: { type: "f", value: 131 / 255 }
        }
      });
      this.mesh = new THREE.Mesh(geometry, material);
      this.scene.add(this.mesh);
      const interval = 3 + delay;

      // explode in
      this.tweenr.to(material.uniforms.animate, {
        value: 1,
        duration: 1.5,
        delay: delay,
        ease: "expoInOut"
      });
      this.tweenr.to(material.uniforms.scale, {
        value: 1,
        duration: 1,
        delay: delay
      });

      // explode out
      this.tweenr.to(material.uniforms.scale, {
        delay: interval,
        value: 0,
        duration: 0.75,
        ease: "expoIn"
      });
      this.tweenr
        .to(material.uniforms.animate, {
          duration: 0.75,
          value: 0,
          delay: interval
        })
        .on("complete", () => {
          geometry.dispose();
          this.scene.remove(this.mesh);
          this.mesh = null;
          this.nextSvgMesh(0);
        });
    },
    getAnimationAttributes(positions, cells) {
      const directions = new Float32Array(cells.length * 9);
      const centroids = new Float32Array(positions.length * 3);
      for (let i = 0; i < positions.length * 3; i++) {
        let f0, f1, f2;
        if (cells[i]) {
          [f0, f1, f2] = cells[i];
        } else {
          [f0, f1, f2] = [0, 0, 0];
        }
        const triangle = [positions[f0], positions[f1], positions[f2]];
        const center = triangleCentroid(triangle);
        const dir = new THREE.Vector3().fromArray(center);
        centroids[i * 3] = dir.x;
        centroids[i * 3 + 1] = dir.y;
        centroids[i * 3 + 2] = dir.z;
      }
      for (let j = 0; j < cells.length * 9; j++) {
        const random = randomVec3([], Math.random());
        const anim = new THREE.Vector3().fromArray(random);
        directions[j * 3] = anim.x;
        directions[j * 3 + 1] = anim.y;
        directions[j * 3 + 2] = anim.z;
      }
      return {
        direction: { type: "v3", value: directions },
        centroid: { type: "v3", value: centroids }
      };
    },
    createApp() {
      this.app = createLoop(this.canvas, {
        scale: this.renderer.devicePixelRatio
      })
        .start()
        .on("tick", this.render)
        .on("resize", this.resize);

      this.resize();
    },
    resize() {
      const [width, height] = this.app.shape;
      this.camera.aspect = width / height;
      this.renderer.setSize(width, height, false);
      this.camera.updateProjectionMatrix();
      this.render();
    },
    render(time) {
      if (this.mesh) {
        const newTime = time * 0.00011;
        const rotation = newTime;
        this.mesh.rotation.y += rotation;
      }
      this.renderer.render(this.scene, this.camera);
    }
  },
  beforeDestroy() {
    this.scene.dispose();
  }
};
</script>

<style lang="scss">
.svg-three-animation {
  &__wrapper {
    height: 100%;
  }
  &__container {
    height: 100%;
  }
}
</style>
